import React from "react";
import { dateFormatter } from "../../../utils/UtilityFunctions";
import { MOMENT_PAIRS as MOMENT_KEYS, hasScore } from "../../../utils/dayProgress";

const PRIMING_FIELDS = [
  ["thankYou", "Thank You"],
  ["selfLess", "Selfless Act"],
  ["mindBody", "Mind & Body"],
];

const PROCESSING_FIELDS = [
  ["rememberToday", "Remember Today"],
  ["leaveBehind", "Leave Behind"],
  ["onMyMind", "On My Mind"],
];

const formatScore = (val) => (hasScore(val) ? val : "—");

const formatDate = (day) => {
  if (!day || !day.logDate) return "";
  return dateFormatter(day.logDate.slice(0, 10));
};

const SingleDayView = ({ day, onVisit, onDelete, onBack, viewType, scoreOpacity }) => {
  const hasStartScore = hasScore(day.startScore);
  const hasEndScore = hasScore(day.endScore);

  return (
    <>
      <div className="log__preview-header">
        {viewType === "mobile" && (
          <button className="log__preview-btn log__preview-btn--back" onClick={onBack}>
            ←
          </button>
        )}
        <h3>{formatDate(day)}</h3>
        {(hasStartScore || hasEndScore) && (
          <div className="log__preview-scores-inline">
            {hasStartScore && (
              <div className="log__preview-score-inline-item">
                <span className="log__preview-score-inline-label">Before</span>
                <span
                  className="log__preview-score-inline-badge"
                  style={{ backgroundColor: `rgba(226, 223, 207, ${scoreOpacity(day.startScore)})` }}
                >
                  {day.startScore}
                </span>
              </div>
            )}
            {hasEndScore && (
              <div className="log__preview-score-inline-item">
                <span className="log__preview-score-inline-label">After</span>
                <span
                  className="log__preview-score-inline-badge"
                  style={{ backgroundColor: `rgba(226, 223, 207, ${scoreOpacity(day.endScore)})` }}
                >
                  {day.endScore}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="log__preview-actions">
          <button
            className="log__preview-btn log__preview-btn--visit"
            onClick={() => onVisit(day.id)}
          >
            Open Day
          </button>
          <button
            className="log__preview-btn log__preview-btn--delete"
            onClick={() => onDelete(day.id)}
          >
            Delete
          </button>
        </div>
      </div>

      {PRIMING_FIELDS.some(([key]) => day[key]) && (
        <div className="log__preview-section">
          <div className="log__preview-section-label">Priming the Day</div>
          {PRIMING_FIELDS.map(([key, label]) =>
            day[key] ? (
              <div key={key} className="log__field-row">
                <div className="log__field-label">{label}</div>
                <div className="log__field-value">{day[key]}</div>
              </div>
            ) : null
          )}
        </div>
      )}

      {MOMENT_KEYS.some(([textKey]) => day[textKey]) && (
        <div className="log__preview-section">
          <div className="log__preview-section-label">My Plan for the Day</div>
          {MOMENT_KEYS.map(([textKey, scoreKey]) =>
            day[textKey] ? (
              <div key={textKey} className="log__moment-row">
                <span className="log__moment-text">{day[textKey]}</span>
                {day[scoreKey] != null && day[scoreKey] !== "" && (
                  <span className="log__moment-score">{day[scoreKey]}</span>
                )}
              </div>
            ) : null
          )}
        </div>
      )}

      {PROCESSING_FIELDS.some(([key]) => day[key]) && (
        <div className="log__preview-section">
          <div className="log__preview-section-label">Reflecting on the Day</div>
          {PROCESSING_FIELDS.map(([key, label]) =>
            day[key] ? (
              <div key={key} className="log__field-row">
                <div className="log__field-label">{label}</div>
                <div className="log__field-value">{day[key]}</div>
              </div>
            ) : null
          )}
        </div>
      )}
    </>
  );
};

const ComparisonView = ({ compareDays, onVisit, onBack, viewType, scoreOpacity }) => (
  <>
    {viewType === "mobile" && (
      <div className="log__preview-header">
        <button className="log__preview-btn log__preview-btn--back" onClick={onBack}>
          ←
        </button>
        <h3>Comparing {compareDays.length} Days</h3>
      </div>
    )}
    <div
      className="log__compare-grid"
      style={{ "--compare-cols": compareDays.length }}
    >
      {compareDays.map((day) => (
        <div key={day.id} className="log__compare-col">
          <div className="log__compare-col-header">
            <span>{formatDate(day)}</span>
          </div>

          <div className="log__compare-scores">
            <div className="log__compare-score-item">
              <span className="log__compare-score-label">Before</span>
              <span
                className="log__compare-score-badge"
                style={{ backgroundColor: `rgba(0, 74, 35, ${scoreOpacity(day.startScore)})` }}
              >
                {formatScore(day.startScore)}
              </span>
            </div>
            <div className="log__compare-score-item">
              <span className="log__compare-score-label">After</span>
              <span
                className="log__compare-score-badge"
                style={{ backgroundColor: `rgba(0, 74, 35, ${scoreOpacity(day.endScore)})` }}
              >
                {formatScore(day.endScore)}
              </span>
            </div>
          </div>

          {MOMENT_KEYS.some(([textKey]) => day[textKey]) && (
            <>
              <div className="log__compare-field-label">Moments</div>
              {MOMENT_KEYS.map(([textKey]) =>
                day[textKey] ? (
                  <div key={textKey} className="log__compare-moment">
                    {day[textKey]}
                  </div>
                ) : null
              )}
            </>
          )}

          {day.rememberToday && (
            <>
              <div className="log__compare-field-label">Remember Today</div>
              <div className="log__compare-remember">{day.rememberToday}</div>
            </>
          )}

          <button
            className="log__compare-visit"
            onClick={() => onVisit(day.id)}
          >
            Open Day
          </button>
        </div>
      ))}
    </div>
  </>
);

export const DayPreviewPanel = ({
  day,
  compareDays,
  compareMode,
  onVisit,
  onDelete,
  onBack,
  isOpen,
  viewType,
  scoreOpacity,
}) => {
  const showComparison = compareMode && compareDays.length >= 2;
  const panelClass = `log__preview-panel${isOpen ? " log__preview-panel--open" : ""}`;

  if (!day && !showComparison) {
    return (
      <div className={panelClass}>
        <div className="log__preview-placeholder">
          <p>Select a day from the list to preview it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={panelClass}>
      {showComparison ? (
        <ComparisonView
          compareDays={compareDays}
          onVisit={onVisit}
          onBack={onBack}
          viewType={viewType}
          scoreOpacity={scoreOpacity}
        />
      ) : (
        <SingleDayView
          day={day}
          onVisit={onVisit}
          onDelete={onDelete}
          onBack={onBack}
          viewType={viewType}
          scoreOpacity={scoreOpacity}
        />
      )}
    </div>
  );
};
